import { capitalize } from 'vue';

import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import Creative from '@/common/models/creative/Creative';
import CreativeFile from '@/common/models/creative/CreativeFile';
import CreativeMediaApproval from '@/common/models/creative/CreativeMediaApproval';
import InventoryTargeting from '@/common/models/InventoryTargeting';
import MediaOwnerApprovalStatus from '@/common/models/media-owner/MediaOwnerApprovalStatus.enum';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';
import UserNotifications from '@/utils/UserNotifications';
import i18n from '@/vueI18n';

export class CreativeMediaOwnerApprovalService {
  public static async getCreativesInventoryTargetingUnion(creativeMediaApprovalIDs: number[]) {
    const { getCreativeMediaOwnerApprovalInventoryTargeting } = mapOrgAppActions();
    const creativeInventoryTargetings = new Array<InventoryTargeting>();
    for (const id of creativeMediaApprovalIDs) {
      try {
        const { creative } = await getCreativeMediaOwnerApprovalInventoryTargeting({
          id,
          showLoader: true,
        });
        creativeInventoryTargetings.push(creative.inventoryTargeting);
      } catch (error) {
        console.error(error);
      }
    }

    return getInventoryTargetingUnion(creativeInventoryTargetings);
  }

  public static async approveCreativeMediaApprovals(
    creativeMediaApprovalIDs: number[],
    inventoryTargeting: InventoryTargeting = new InventoryTargeting(),
  ): Promise<boolean> {
    const { organization } = mapOrgAppState();

    const ownerID = organization.id || -1;

    const { updateCreativeMediaOwnerApproval } = mapOrgAppActions();

    try {
      for (const id of creativeMediaApprovalIDs) {
        await updateCreativeMediaOwnerApproval({
          data: {
            creative_mediaowner_approval_id: id,
            owner_id: ownerID,
            state: MediaOwnerApprovalStatus.APPROVED,
            inventory_targeting: inventoryTargeting?.toApi() || {},
          },
          params: { id },
        });
      }
      UserNotifications.success({
        title: capitalize(i18n.global.t('names.success')),
        message: i18n.global.t('messages.creativeApprovedSuccessfully', { n: creativeMediaApprovalIDs.length }),
      });
      return true;
    } catch (error) {
      GlobalEventBus.$emit(GlobalEvents.somethingWentWrong);
      throw error;
    }
  }

  public static getCreativeFilesMediaOwnerApprovals(
    creativeFiles: CreativeFile[],
    creatives: Creative[],
  ): CreativeMediaApproval[] {
    const { organization } = mapOrgAppState();
    const ownerID = organization?.id || -1;
    const creativeMediaApprovals = new Array<CreativeMediaApproval>();
    for (const creativeFile of creativeFiles) {
      // Find parent creative for the selected creative file
      const creative = creatives.find((creative) => creative.id === creativeFile.creativeID);
      // Find the media owner approval for the selected creative file
      const creativeMediaApproval = creative.mediaOwnerApprovals.find(
        (mediaOwnerApproval) =>
          mediaOwnerApproval.ownerID === ownerID && mediaOwnerApproval.creativefileID === creativeFile.id,
      );
      if (creativeMediaApproval != null) {
        creativeMediaApprovals.push(creativeMediaApproval);
      }
    }

    return creativeMediaApprovals;
  }
}

/**
 * Represents the keys of an object that are of type string[]
 */
type KeysOfStringArray<T> = {
  [K in keyof T]: T[K] extends string[] ? K : never;
}[keyof T];

/**
 * Represents the keys of an object that are of type number[]
 */
type KeysOfNumberArray<T> = {
  [K in keyof T]: T[K] extends number[] ? K : never;
}[keyof T];

/**
 * Represents the keys of `InventoryTargeting` that are of type string[] or number[]
 */
type InventoryTargetingKeys = KeysOfNumberArray<InventoryTargeting> | KeysOfStringArray<InventoryTargeting>;

/**
 * Return `string` if K is a key of a `string[]` property and `number` if K is a key of a `number[]` property
 * @example
 * ```
 * InventoryTargetingPropertyType['locationIDs'] // number
 * InventoryTargetingPropertyType['unitTags'] // string
 * ```
 */
type InventoryTargetingPropertyType<K extends InventoryTargetingKeys> =
  K extends KeysOfStringArray<InventoryTargeting>
    ? string
    : K extends KeysOfNumberArray<InventoryTargeting>
      ? number
      : never;

/**
 * @param key The key of the `InventoryTargeting` object to get the union of
 * @param inventoryTargetings The array of `InventoryTargeting` objects to get the union from
 * @returns The union of the specified property from all `InventoryTargeting` objects
 */
function getPropertyUnion<K extends InventoryTargetingKeys>(
  key: K,
  inventoryTargetings: InventoryTargeting[],
): InventoryTargetingPropertyType<K>[] {
  const set = new Set<InventoryTargetingPropertyType<K>>();
  for (const inventoryTargeting of inventoryTargetings) {
    if (inventoryTargeting[key] != null) {
      for (const id of inventoryTargeting[key]) {
        set.add(id as InventoryTargetingPropertyType<K>);
      }
    }
  }
  return Array.from(set);
}

/**
 * Get the union of all inventory targeting objects
 */
function getInventoryTargetingUnion(inventoryTargetings: InventoryTargeting[]): InventoryTargeting {
  const union = new InventoryTargeting();

  union.locationIDs = getPropertyUnion('locationIDs', inventoryTargetings);
  union.locationExclusionIDs = getPropertyUnion('locationExclusionIDs', inventoryTargetings);
  union.geofenceGroupIDs = getPropertyUnion('geofenceGroupIDs', inventoryTargetings);
  union.geofenceGroupExclusionIDs = getPropertyUnion('geofenceGroupExclusionIDs', inventoryTargetings);
  union.languageIDs = getPropertyUnion('languageIDs', inventoryTargetings);
  union.languageExclusionIDs = getPropertyUnion('languageExclusionIDs', inventoryTargetings);
  union.marketIDs = getPropertyUnion('marketIDs', inventoryTargetings);
  union.marketExclusionIDs = getPropertyUnion('marketExclusionIDs', inventoryTargetings);
  union.mediaTypeIDs = getPropertyUnion('mediaTypeIDs', inventoryTargetings);
  union.mediaTypeExclusionIDs = getPropertyUnion('mediaTypeExclusionIDs', inventoryTargetings);
  union.networkIDs = getPropertyUnion('networkIDs', inventoryTargetings);
  union.networkExclusionIDs = getPropertyUnion('networkExclusionIDs', inventoryTargetings);
  union.mediaTypeIDs = getPropertyUnion('mediaTypeIDs', inventoryTargetings);
  union.mediaTypeExclusionIDs = getPropertyUnion('mediaTypeExclusionIDs', inventoryTargetings);
  union.siteIDs = getPropertyUnion('siteIDs', inventoryTargetings);
  union.siteExclusionIDs = getPropertyUnion('siteExclusionIDs', inventoryTargetings);
  union.unitIDs = getPropertyUnion('unitIDs', inventoryTargetings);
  union.unitExclusionIDs = getPropertyUnion('unitExclusionIDs', inventoryTargetings);
  union.unitPackIDs = getPropertyUnion('unitPackIDs', inventoryTargetings);
  union.unitPackExclusionIDs = getPropertyUnion('unitPackExclusionIDs', inventoryTargetings);
  union.unitTags = getPropertyUnion('unitTags', inventoryTargetings);
  union.unitTagExclusions = getPropertyUnion('unitTagExclusions', inventoryTargetings);
  union.creativeFormats = getPropertyUnion('creativeFormats', inventoryTargetings);
  union.creativeFormatExclusions = getPropertyUnion('creativeFormatExclusions', inventoryTargetings);
  union.creativeOrientations = getPropertyUnion(
    'creativeOrientations',
    inventoryTargetings,
  ) as CreativeOrientationType[];
  union.creativeOrientationExclusions = getPropertyUnion(
    'creativeOrientationExclusions',
    inventoryTargetings,
  ) as CreativeOrientationType[];

  return union;
}
